import QRious from 'qrious';

export const isMobile = (userAgent: string) => {
  return !!/Android|webOS|iPhone|iPad|iPod|BB10|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(userAgent || '');
};

export const isTablet = (userAgent: string) => {
  return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
    userAgent
  );
};

export const isIpad = (userAgent: string) => {
  return /macintosh/.test(userAgent) && 'ontouchend' in document;
};

export const isIOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const openURL = (url: string, blank = false) => {
  blank ? window.open(url, '_blank') : (window.location.href = url);
};

export const setCookie = (name: string, value: string, expDays: number) => {
  let date = new Date();
  date.setTime(date.getTime() + expDays * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + date.toUTCString();
  document.cookie = name + '=' + value + '; ' + expires + '; path=/';
};

export const getCookie = (cName: string) => {
  const name = cName + '=';
  const cDecoded = decodeURIComponent(document.cookie);
  const cArr = cDecoded.split('; ');
  let res;
  cArr.forEach((val) => {
    if (val.indexOf(name) === 0) res = val.substring(name.length);
  });
  return res;
};

export const getElementById = (id: string) => {
  return document.getElementById(id);
};

export const speechText = (msg: string) => {
  const speech = new SpeechSynthesisUtterance();
  speech.text = msg;
  speech.rate = 0.9; // Adjust speech rate
  window.speechSynthesis.speak(speech);
};

/**
 * Downloads a QR code as a PNG file
 * @param value - The text/URL to encode in the QR code
 * @param options - Optional configuration
 * @param options.size - QR code size in pixels (default: 256)
 * @param options.filename - Download filename (default: `qr-code-${Date.now()}.png`)
 */
export const downloadQRCode = (value: string, options?: { size?: number; filename?: string }): void => {
  if (!value) return;

  try {
    const size = options?.size ?? 256;
    const filename = options?.filename ?? `qr-code-${Date.now()}.png`;

    // Generate QR code
    const canvas = document.createElement('canvas');
    canvas.style.display = 'none';
    document.body.appendChild(canvas);

    try {
      const qr = new QRious({
        element: canvas,
        value,
        size,
      });

      // Get data URL from canvas
      const qrDataUrl = canvas.toDataURL();

      // Convert data URL to blob
      const byteString = atob(qrDataUrl.split(',')[1]);
      const mimeString = qrDataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      // Cleanup canvas - only remove if it's still attached to document.body
      if (canvas.parentNode === document.body) {
        document.body.removeChild(canvas);
      }
    }
  } catch (error) {
    console.error('Error downloading QR code:', error);
  }
};

export const isSamsungDevice = () => {
  const ua = navigator.userAgent;
  return (
    /SM-[A-Z0-9]{3,}/i.test(ua) || // Standard Samsung model numbers
    /\(Linux; Android [^;]+; K\)/.test(ua) ||
    /Samsung/i.test(ua) || // Explicit Samsung mention
    /SEC/i.test(ua) || // Samsung Electronics Company
    /SC-0[0-9]/i.test(ua) // Some Samsung models
  );
};

export const isMacOS = () => {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.includes('Mac');
};

export const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};
